<template>
	<BlockVisibilityObserver title="REVIEWS">
		<section class="kova-v2-testimonials">
			<h2 class="kova-v2-testimonials__title">{{ title }}</h2>

			<LandingKovaCarousel
				v-slot="{ duplicate }"
				:count="items.length"
				item-name="review"
				:label="title"
				loop
				theme="testimonials"
			>
				<article
					v-for="(item, index) in items"
					:key="item.name + index"
					:aria-label="`Review ${index + 1} of ${items.length}`"
					:aria-hidden="duplicate || undefined"
					:inert="duplicate || undefined"
					class="kova-carousel__slide kova-v2-testimonials__card"
				>
					<div class="kova-v2-testimonials__shots">
						<span v-for="(shot, shotIndex) in SHOTS[item.customer]" :key="shot" class="kova-v2-testimonials__shot">
							<img
								:alt="`${item.name} ${shotIndex === 0 ? 'before' : 'after'}`"
								class="kova-v2-testimonials__photo"
								decoding="async"
								loading="lazy"
								:src="shot"
							/>
						</span>
					</div>

					<div class="kova-v2-testimonials__text">
						<div class="kova-v2-testimonials__meta">
							<h3 class="kova-v2-testimonials__name">
								<span v-text="`${item.name} —`" />
								<span class="kova-v2-testimonials__result" v-text="` ${item.result}`" />
							</h3>
							<span aria-hidden="true" class="kova-v2-testimonials__stars">
								<span v-for="star in 5" :key="star" class="kova-v2-testimonials__star" />
							</span>
						</div>
						<p class="kova-v2-testimonials__quote">{{ item.quote }}</p>
					</div>
				</article>
			</LandingKovaCarousel>
		</section>
	</BlockVisibilityObserver>
</template>

<script setup lang="ts">
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaCarousel from '@/modules/Landing/components/LandingKovaCarousel.vue'
import { LandingComponentProps } from '@/modules/Landing/instances/@types/landing-types'

import resultBrianAfter from '@/assets/kova/landing/result-brian-after.jpg'
import resultBrianBefore from '@/assets/kova/landing/result-brian-before.jpg'
import resultChrisAfter from '@/assets/kova/landing/result-chris-after.jpg'
import resultChrisBefore from '@/assets/kova/landing/result-chris-before.jpg'
import resultJamesAfter from '@/assets/kova/landing/result-james-after.jpg'
import resultJamesBefore from '@/assets/kova/landing/result-james-before.jpg'
import resultRachelAfter from '@/assets/kova/landing/result-rachel-after.jpg'
import resultRachelBefore from '@/assets/kova/landing/result-rachel-before.jpg'
import resultSarahAfter from '@/assets/kova/landing/result-sarah-after.jpg'
import resultSarahBefore from '@/assets/kova/landing/result-sarah-before.jpg'

defineOptions({ name: 'LandingKovaV2Testimonials' })

const SHOTS = {
	sarah: [resultSarahBefore, resultSarahAfter],
	james: [resultJamesBefore, resultJamesAfter],
	rachel: [resultRachelBefore, resultRachelAfter],
	chris: [resultChrisBefore, resultChrisAfter],
	brian: [resultBrianBefore, resultBrianAfter],
}

defineProps<LandingComponentProps.KovaV2Testimonials>()
</script>

<style lang="scss" scoped>
// Mobile (6415:53518): 20 top, 32 bottom, 16px gutters; one card per view.
.kova-v2-testimonials {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding: var(--size-20) var(--size-16) var(--size-32);

	font-family: var(--kova-lp-font);
}

.kova-v2-testimonials__title {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: var(--size-36); // 32px × 1.12 = 35.84px — exact size not in design system
	letter-spacing: -0.64px;
	text-align: center;

	color: var(--kova-lp-ink);
}

// 6415:53521 — white card, 16 padding, 24 radius.
.kova-v2-testimonials__card {
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: var(--size-16);
	padding: var(--size-16);

	border-radius: var(--size-24);
	background: var(--Fill-White);
}

// 6415:53522 — two 295px frames, 4px apart, 8 radius.
.kova-v2-testimonials__shots {
	display: flex;
	gap: var(--size-4);
	height: 295px; // 6415:53522 — exact size not in design system
}

.kova-v2-testimonials__shot {
	position: relative;
	overflow: hidden;
	flex: 1 1 0;
	min-width: 0;

	border-radius: var(--size-8);
	background: var(--Fill-White);
}

.kova-v2-testimonials__photo {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.kova-v2-testimonials__text,
.kova-v2-testimonials__meta {
	display: flex;
	flex-direction: column;
	gap: var(--size-12);
}

// Figma 6415:53528 uses SF Pro Text 16/normal; use the project text face and size scale.
.kova-v2-testimonials__name {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-16);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.32px;

	color: var(--Text-Primary-100);
}

.kova-v2-testimonials__result {
	color: var(--kova-lp-highlight);
}

.kova-v2-testimonials__stars {
	display: flex;
	gap: var(--size-2);
}

// 6415:53530 / 6557:32381 — the DS star (`star-light.svg`, the shared rating star) in
// brand/shot/shot-accent: 16x15 here, 20x19 on desktop.
.kova-v2-testimonials__star {
	display: block;
	width: var(--size-16);
	height: 15px; // 6415:53530 — exact size not in design system

	background: var(--kova-lp-check);
	mask: url('@/assets/kova/landing/star-light.svg') center / contain no-repeat;
}

.kova-v2-testimonials__quote {
	margin: 0;

	font-size: var(--size-16);
	line-height: var(--size-24); // 16px × 1.44 = 23.04px — exact size not in design system

	color: var(--Text-Primary-100);
}

// Desktop (6547:17082): 60px block padding, a full-bleed rail of 400px cards 16 apart,
// clipped by the page edges (first card at x=-312 at 1440).
@media (min-width: 1024px) {
	.kova-v2-testimonials {
		gap: var(--size-40);
		padding: var(--size-60) 0;
	}

	.kova-v2-testimonials__title {
		padding-inline: var(--size-40);

		font-size: var(--size-56);
		line-height: var(--size-60); // 56px × 1.1 = 61.60px — exact size not in design system
		letter-spacing: -1.12px;
	}

	// 6557:32371 — 400x558, 20/20/24 padding, 32 radius.
	.kova-v2-testimonials__card {
		gap: var(--size-20);
		min-height: 558px; // 6557:32371 — exact size not in design system
		padding: var(--size-20) var(--size-20) var(--size-24);

		border-radius: var(--size-32);
	}

	.kova-v2-testimonials__shots {
		gap: var(--size-8);
	}

	.kova-v2-testimonials__shot {
		border-radius: var(--size-16);
	}

	.kova-v2-testimonials__text {
		gap: var(--size-16);
	}

	// 6557:32379 — Inter SemiBold 18/22 on desktop.
	.kova-v2-testimonials__name {
		font-family: var(--font-family);
		font-size: var(--size-18);
		font-weight: 600;
		line-height: var(--size-22);
		letter-spacing: 0;
	}

	.kova-v2-testimonials__star {
		width: var(--size-20);
		height: var(--size-20); // Closest to 19px in Figma 6557:32381.
	}

	.kova-v2-testimonials__quote {
		font-size: var(--size-18);
		line-height: var(--size-26); // 18px × 1.44 = 25.92px — exact size not in design system
	}
}
</style>
